import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();
  
  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    // Reset services submenu when main menu is closed
    if (!isMenuOpen === false) {
      setIsServicesOpen(false);
    }
  };

  const toggleServices = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsServicesOpen(!isServicesOpen);
  };

  // Check if the current path matches the link
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Check if the current path starts with the services path
  const isServiceActive = (path: string) => {
    return location.pathname.startsWith('/services/');
  };

  return (
    <header className="bg-white shadow-lg fixed w-full z-50 border-b-2 border-gray-100">
      <div className="container mx-auto px-6 py-1">

        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src="/images/ZOLO-LOGO-PNG-768x298.png" alt="ZOLO" className="h-12" style={{ maxWidth: '180px' }} />
          </Link>
          

          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center">
            <nav className="flex space-x-8 mr-8">
              <Link to="/" className={`text-gray-800 hover:text-zolo-blue font-medium ${isActive('/') ? 'text-zolo-blue font-semibold' : ''}`}>Home</Link>
              <Link to="/about" className={`text-gray-800 hover:text-zolo-blue font-medium ${isActive('/about') ? 'text-zolo-blue font-semibold' : ''}`}>About Us</Link>
              
              {/* Services Dropdown */}
              <div className="flex items-center space-x-8">
                <div className="relative">
                  <button 
                    className={`text-gray-800 hover:text-zolo-blue font-medium flex items-center ${isServiceActive('/services/') ? 'text-zolo-blue font-semibold' : ''}`}
                    onClick={() => setIsServicesOpen(!isServicesOpen)}
                  >
                    Services
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className={`h-4 w-4 ml-1 transition-transform duration-200 ${isServicesOpen ? 'transform rotate-180' : ''}`}
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {isServicesOpen && (
                  <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                      <Link to="/services/web-development" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-zolo-blue">Web Development</Link>
                      <Link to="/services/social-media-marketing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-zolo-blue">Social Media Marketing</Link>
                      <Link to="/services/graphic-designing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-zolo-blue">Graphic Designing</Link>
                      <Link to="/services/seo-services" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-zolo-blue">SEO Services</Link>
                      <Link to="/services/whatsapp-marketing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-zolo-blue">WhatsApp Marketing</Link>
                      <Link to="/services/advertisement" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-zolo-blue">Advertisement</Link>
                      <Link to="/services/google-my-business" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-zolo-blue">Google My Business</Link>
                      <Link to="/services/influencer-marketing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-zolo-blue">Influencer Marketing</Link>
                    </div>
                  </div>
                  )}
                </div>
                
                <Link to="/packages" className={`text-gray-800 hover:text-zolo-blue font-medium ${isActive('/packages') ? 'text-zolo-blue font-semibold' : ''}`}>Packages</Link>
                <Link to="/contact" className={`text-gray-800 hover:text-zolo-blue font-medium ${isActive('/contact') ? 'text-zolo-blue font-semibold' : ''}`}>Contact</Link>
              </div>
            </nav>
            
            {/* Social Media Icons - Desktop */}
            <div className="flex space-x-3 border-l border-gray-200 pl-6">
              <a href="https://www.facebook.com/zolodigitaladvertising" target="_blank" rel="noopener noreferrer" className="bg-gray-100 hover:bg-zolo-blue hover:text-white text-gray-600 p-2 rounded-full transition duration-300">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
                </svg>
              </a>
              <a href="https://www.instagram.com/zolodigital_chhindwara/" target="_blank" rel="noopener noreferrer" className="bg-gray-100 hover:bg-zolo-orange hover:text-white text-gray-600 p-2 rounded-full transition duration-300">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
              <a href="https://www.youtube.com/@zolodigitaladvertising3845" target="_blank" rel="noopener noreferrer" className="bg-gray-100 hover:bg-red-600 hover:text-white text-gray-600 p-2 rounded-full transition duration-300">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                </svg>
              </a>
            </div>
          </div>
          
          {/* Mobile Menu Button */}
          <div className="md:hidden mr-1">
            <button 
              onClick={toggleMenu}
              className="text-gray-800 hover:text-zolo-blue focus:outline-none w-10 h-10 flex items-center justify-center"
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-6 w-6 transition-all duration-300" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
        
        {/* Mobile Navigation - Left Slide Overlay */}
        <div 
          className={`fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden transition-opacity duration-300 ${
            isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          onClick={toggleMenu}
        >
          <div 
            className={`fixed inset-y-0 left-0 w-4/5 max-w-sm bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto ${
              isMenuOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
            style={{ top: '0', paddingTop: '0' }}
          >
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <Link to="/" onClick={() => setIsMenuOpen(false)}>
                <img src="/images/ZOLO-LOGO-PNG-768x298.png" alt="ZOLO" className="h-10" />
              </Link>
              <button 
                onClick={toggleMenu}
                className="w-10 h-10 flex items-center justify-center text-gray-800 hover:text-zolo-blue focus:outline-none"
                aria-label="Close menu"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-6 w-6 transition-all duration-300" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <nav className="p-4">
              <ul className="space-y-4">
                <li>
                  <Link 
                    to="/" 
                    className={`text-base font-medium flex items-center py-2 px-3 rounded-lg transition-colors duration-200 ${
                      isActive('/') 
                        ? 'text-white bg-gradient-to-r from-zolo-blue to-zolo-orange' 
                        : 'text-gray-800 hover:text-zolo-blue hover:bg-gray-50'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    Home
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/about" 
                    className={`text-base font-medium flex items-center py-2 px-3 rounded-lg transition-colors duration-200 ${
                      isActive('/about') 
                        ? 'text-white bg-gradient-to-r from-zolo-blue to-zolo-orange' 
                        : 'text-gray-800 hover:text-zolo-blue hover:bg-gray-50'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    About Us
                  </Link>
                </li>
                
                {/* Mobile Services Dropdown */}
                <li>
                  <div className="relative">
                    <button 
                      className={`text-base font-medium flex items-center justify-between w-full py-2 px-3 rounded-lg transition-colors duration-200 ${
                        isServiceActive('/services/') 
                          ? 'text-white bg-gradient-to-r from-zolo-blue to-zolo-orange' 
                          : 'text-gray-800 hover:text-zolo-blue hover:bg-gray-50'
                      }`}
                      onClick={toggleServices}
                    >
                      <div className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>Services</span>
                      </div>
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        className={`h-5 w-5 transition-transform duration-300 ${isServicesOpen ? 'transform rotate-180' : ''}`}
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    <div 
                      className={`mt-1 overflow-hidden transition-all duration-300 ease-in-out ${
                        isServicesOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="pl-3 ml-3 border-l-2 border-zolo-blue space-y-2 py-2">
                        <Link 
                          to="/services/web-development" 
                          className="flex items-center text-sm text-gray-700 hover:text-zolo-blue py-2 px-2 rounded transition-colors duration-200 hover:bg-gray-50"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                          </svg>
                          Web Development
                        </Link>
                        <Link 
                          to="/services/social-media-marketing" 
                          className="flex items-center text-sm text-gray-700 hover:text-zolo-blue py-2 px-2 rounded transition-colors duration-200 hover:bg-gray-50"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                          </svg>
                          Social Media Marketing
                        </Link>
                        <Link 
                          to="/services/graphic-designing" 
                          className="flex items-center text-sm text-gray-700 hover:text-zolo-blue py-2 px-2 rounded transition-colors duration-200 hover:bg-gray-50"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                          Graphic Designing
                        </Link>
                        <Link 
                          to="/services/seo-services" 
                          className="flex items-center text-sm text-gray-700 hover:text-zolo-blue py-2 px-2 rounded transition-colors duration-200 hover:bg-gray-50"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          SEO Services
                        </Link>
                        <Link 
                          to="/services/whatsapp-marketing" 
                          className="flex items-center text-sm text-gray-700 hover:text-zolo-blue py-2 px-2 rounded transition-colors duration-200 hover:bg-gray-50"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          WhatsApp Marketing
                        </Link>
                        <Link 
                          to="/services/advertisement" 
                          className="flex items-center text-sm text-gray-700 hover:text-zolo-blue py-2 px-2 rounded transition-colors duration-200 hover:bg-gray-50"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                          </svg>
                          Advertisement
                        </Link>
                        <Link 
                          to="/services/google-my-business" 
                          className="flex items-center text-sm text-gray-700 hover:text-zolo-blue py-2 px-2 rounded transition-colors duration-200 hover:bg-gray-50"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                          </svg>
                          Google My Business
                        </Link>
                        <Link 
                          to="/services/influencer-marketing" 
                          className="flex items-center text-sm text-gray-700 hover:text-zolo-blue py-2 px-2 rounded transition-colors duration-200 hover:bg-gray-50"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          Influencer Marketing
                        </Link>
                      </div>
                    </div>
                  </div>
                </li>
                
                <li>
                  <Link 
                    to="/packages" 
                    className={`text-base font-medium flex items-center py-2 px-3 rounded-lg transition-colors duration-200 ${
                      isActive('/packages') 
                        ? 'text-white bg-gradient-to-r from-zolo-blue to-zolo-orange' 
                        : 'text-gray-800 hover:text-zolo-blue hover:bg-gray-50'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    Packages
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/contact" 
                    className={`text-base font-medium flex items-center py-2 px-3 rounded-lg transition-colors duration-200 ${
                      isActive('/contact') 
                        ? 'text-white bg-gradient-to-r from-zolo-blue to-zolo-orange' 
                        : 'text-gray-800 hover:text-zolo-blue hover:bg-gray-50'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Contact
                  </Link>
                </li>
              </ul>
            </nav>
            
            {/* Social Media Links */}
            <div className="absolute bottom-0 w-full p-4 border-t border-gray-200">
              <div className="flex space-x-6 justify-center">
                <a 
                  href="https://www.facebook.com/zolodigitaladvertising" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-zolo-blue hover:text-zolo-orange transition-colors duration-200"
                  aria-label="Facebook"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
                  </svg>
                </a>
                <a 
                  href="https://www.instagram.com/zolodigital_chhindwara/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-zolo-blue hover:text-zolo-orange transition-colors duration-200"
                  aria-label="Instagram"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
                <a 
                  href="https://www.youtube.com/@zolodigitaladvertising3845" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-zolo-blue hover:text-zolo-orange transition-colors duration-200"
                  aria-label="YouTube"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
